import React, { useState, useRef, useEffect, useMemo } from "react";
import SharedCTASection from "../../SharedCTASection";
import { FaPlayCircle, FaTimes } from "react-icons/fa";
import searchImg from "../../../../assets/search.png";
import arrowdown from "../../../../assets/arrow-down.png";
import FreeTrialButton from "../../FreeTrialButton";

const GRADIENTS = [
  "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
  "linear-gradient(135deg, #0f3460 0%, #533483 100%)",
  "linear-gradient(135deg, #2d1b69 0%, #11998e 100%)",
  "linear-gradient(135deg, #360033 0%, #0b8793 100%)",
  "linear-gradient(135deg, #1f4037 0%, #99f2c8 100%)",
  "linear-gradient(135deg, #373b44 0%, #4286f4 100%)",
  "linear-gradient(135deg, #4b1248 0%, #f10711 100%)",
  "linear-gradient(135deg, #200122 0%, #6f0000 100%)",
];

function getGradient(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

function VideoThumbnail({ vidUrl, title, onPlay }) {
  const [frameLoaded, setFrameLoaded] = useState(false);
  const gradient = getGradient(title);

  return (
    <div
      className="relative w-full h-full overflow-hidden cursor-pointer"
      style={{ background: gradient }}
      onClick={onPlay}
    >
      <video
        src={`${vidUrl}#t=2`}
        preload="metadata"
        muted
        playsInline
        onLoadedData={() => setFrameLoaded(true)}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
        style={{ opacity: frameLoaded ? 1 : 0 }}
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/35">
        <FaPlayCircle className="text-white text-[72px] opacity-90" />
      </div>
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.beemxyz.com";

const DemoNavContent = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [search, setSearch] = useState("");
  const [showMore, setShowMore] = useState(false);

  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/common/tutorial-videos?type=demo&platform=marketing`)
      .then((res) => res.json())
      .then((json) => {
        const fetched = (json?.data?.videos ?? []).map((v) => ({
          title: v.headerName,
          desc: "get a closer look at how beɘmz transforms operations and customer experience.",
          category: "demo",
          vidUrl: v.videoUrl,
        }));
        setVideos(fetched);
      })
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  const INITIAL_COUNT = 2;
  const isSearching = search.trim() !== "";

  // When searching, filter all videos; otherwise respect show more/less
  const filtered = useMemo(() => {
    if (isSearching) {
      return videos.filter((v) =>
        v.title.toLowerCase().includes(search.trim().toLowerCase())
      );
    }
    return showMore ? videos : videos.slice(0, INITIAL_COUNT);
  }, [search, videos, showMore, isSearching]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) setPlayingIndex(null);
        });
      },
      { threshold: 0.2 }
    );
    if (section1Ref.current) observer.observe(section1Ref.current);
    if (section2Ref.current) observer.observe(section2Ref.current);
    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40 text-white/50 text-lg lowercase" style={{ backgroundColor: "#020013" }}>
        loading videos...
      </div>
    );
  }

  const descSideStyle = {
    backgroundColor: "#020013",
  };

  const titleStyle = {
    fontFamily: "Inter, sans-serif",
    fontWeight: 700,
    fontSize: "42px",
    lineHeight: "100%",
    letterSpacing: "-0.04em",
    color: "#FFFFFF",
  };

  const descStyle = {
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    fontSize: "24px",
    lineHeight: "100%",
    letterSpacing: "-0.04em",
    color: "#CCC3DA",
    maxWidth: "535px",
  };

  return (
    <>
      {/* Search Section */}
      <div className="flex justify-center items-center gap-3 mt-10 mx-auto pb-20 px-4"
        style={{ backgroundColor: "#020013" }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") setSearch(""); }}
          placeholder="search videos"
          className="lowercase focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
          style={{
            width: "399px",
            height: "45px",
            borderRadius: "24px",
            padding: "0 29px",
            backgroundColor: "rgba(245,245,245,0.27)",
            border: "none",
            fontFamily: "Inter, sans-serif",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "100%",
            letterSpacing: "-0.04em",
            color: "#FFFFFF",
          }}
          aria-label="Search demos"
          ref={searchInputRef}
        />
        <button
          type="button"
          onClick={() => search ? setSearch("") : null}
          className="lowercase transition-opacity hover:opacity-80"
          style={{
            height: "46px",
            borderRadius: "9999px",
            backgroundColor: "#7000FF",
            border: "1px solid rgba(112,0,255,0.5)",
            boxShadow: "0px 0px 12px 0px rgba(112,0,255,0.3)",
            color: "#FFFFFF",
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontSize: "16px",
            paddingLeft: "24px",
            paddingRight: "24px",
            whiteSpace: "nowrap",
          }}
        >
          {search ? "clear" : "search"}
        </button>
      </div>

      {/* Video Rows */}
      {filtered.map((item, idx) => (
        <div key={idx}>
          {idx % 2 === 0 ? (
            <div
              ref={section1Ref}
              id="section1"
              className="flex flex-col md:flex-row items-center justify-center w-full h-screen md:h-[400px] xl-2xl:h-[500px] overflow-hidden"
            >
              {/* Left - Text (dark) */}
              <div className="flex flex-col justify-center items-start w-full md:w-1/2 h-full px-8 md:px-16 space-y-5" style={descSideStyle}>
                <h1 className="lowercase" style={titleStyle}>{item.title}</h1>
                <p className="lowercase" style={descStyle}>{item.desc}</p>
                <span className="mt-0 flex items-start">
                  <FreeTrialButton onClick={() => window.location.href = "/business/get-started"} />
                </span>
              </div>

              {/* Right - Video (untouched) */}
              <div className="relative w-full md:w-1/2 h-full overflow-hidden bg-black">
                {playingIndex !== idx ? (
                  <VideoThumbnail vidUrl={item.vidUrl} title={item.title} onPlay={() => setPlayingIndex(idx)} />
                ) : (
                  <div className="absolute inset-0 bg-black">
                    <button onClick={() => setPlayingIndex(null)} className="absolute top-3 right-3 z-10 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition">
                      <FaTimes />
                    </button>
                    <video src={item.vidUrl} autoPlay controls className="w-full h-full object-contain" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              ref={section2Ref}
              id="section2"
              className="flex flex-col-reverse md:flex-row items-center justify-center w-full h-screen md:h-[400px] xl-2xl:h-[500px] overflow-hidden"
            >
              {/* Left - Video (untouched) */}
              <div className="relative w-full md:w-1/2 h-full overflow-hidden bg-black">
                {playingIndex !== idx ? (
                  <VideoThumbnail vidUrl={item.vidUrl} title={item.title} onPlay={() => setPlayingIndex(idx)} />
                ) : (
                  <div className="absolute inset-0 bg-black">
                    <button onClick={() => setPlayingIndex(null)} className="absolute top-3 right-3 z-10 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition">
                      <FaTimes />
                    </button>
                    <video src={item.vidUrl} autoPlay controls className="w-full h-full object-contain" />
                  </div>
                )}
              </div>

              {/* Right - Text (dark) */}
              <div className="flex flex-col justify-center items-start w-full md:w-1/2 h-full px-8 md:px-16 space-y-5" style={descSideStyle}>
                <h1 className="lowercase" style={titleStyle}>{item.title}</h1>
                <p className="lowercase" style={descStyle}>{item.desc}</p>
                <span className="mt-0 flex items-start">
                  <FreeTrialButton onClick={() => window.location.href = "/business/get-started"} />
                </span>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* View More / View Less */}
      {!isSearching && videos.length > INITIAL_COUNT && (
        <div className="flex justify-center items-center mt-10 pb-[80px]" style={{ backgroundColor: "#020013" }}>
          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className="flex items-center gap-2 px-6 py-3 rounded-full lowercase transition-opacity hover:opacity-80"
            style={{ border: "1px solid rgba(255,255,255,0.2)", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "16px", backgroundColor: "transparent" }}
          >
            <img src={arrowdown} alt="" style={{ filter: "invert(1)", opacity: 0.7 }} />
            {showMore ? "view less" : "view more"}
          </button>
        </div>
      )}

      {filtered.length === 0 && !loading && (
        <div className="py-20 lowercase text-center" style={{ backgroundColor: "#020013", color: "rgba(255,255,255,0.4)", fontFamily: "Inter, sans-serif" }}>
          no match found
        </div>
      )}
      <SharedCTASection />
    </>
  );
};

export default DemoNavContent;
